/**
 * Pre-Conditioning Page
 * ─────────────────────────────────────────────────────────────────────────────
 * Workflow: APPROVED Laydown → Start Conditioning (move bales to pre-godown)
 *           → 7+ day wait → Release Conditioning → READY_FOR_MIX
 *
 * Tab 1: Approved (awaiting conditioning)
 * Tab 2: In Conditioning (countdown)
 * Tab 3: Ready for Mix (proceed to execution)
 */
import { useState } from 'react'
import {
  App, Alert, Badge, Button, Card, Col, Empty, Form, Input, InputNumber,
  Modal, Progress, Row, Space, Statistic, Table, Tabs, Tag, Tooltip, Typography,
} from 'antd'
import {
  PlayCircleOutlined, CheckCircleOutlined, ClockCircleOutlined,
  ExclamationCircleOutlined, CloudUploadOutlined, ThunderboltOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import apiClient from '@/shared/api/client'
import { laydownApi } from '@/features/baleManagement/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'
import dayjs from 'dayjs'

const { Text, Title } = Typography

const STATUS_COLORS: Record<string, string> = {
  APPROVED:           'green',
  CONDITIONING:       'orange',
  READY_FOR_MIX:      'blue',
  MIXING_IN_PROGRESS: 'purple',
}

export default function PreConditioningPage() {
  const { message, modal } = App.useApp()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { plantId } = usePlantId()

  // Sprint Costing followup — Ready-for-Mix → Start Execution wiring.
  // The Pre-Conditioning page used to just show "Use Laydown Execution"
  // as a hint Tag, but the Laydown Execution page can't display anything
  // until an execution row exists. This mutation creates that row and
  // navigates the operator straight to it.
  const startExecutionMut = useMutation({
    mutationFn: (ld_id: string) =>
      apiClient.post(`/bale-mgmt/laydowns/${ld_id}/start-execution`).then(r => r.data),
    onSuccess: (exec: any) => {
      message.success(`Execution ${exec.execution_no || exec.id?.slice(0, 8)} started — opening Laydown Execution`)
      qc.invalidateQueries({ queryKey: ['laydowns-ready'] })
      navigate('/bale-mgmt/laydown-execution')
    },
    onError: (e: any) =>
      message.error(e.response?.data?.detail || 'Failed to start execution'),
  })

  // ── Tab 1: Approved laydowns awaiting conditioning ────────────────────────
  const { data: approved = [], isLoading: loadingApproved } = useQuery({
    queryKey: ['laydowns-approved', plantId],
    queryFn: () => laydownApi.byStatus('APPROVED', plantId),
    enabled: !!plantId,
  })

  // ── Tab 2: Currently in conditioning ───────────────────────────────────────
  const { data: conditioning = [], isLoading: loadingConditioning } = useQuery({
    queryKey: ['conditioning-queue', plantId],
    queryFn: () => laydownApi.conditioningQueue(plantId),
    enabled: !!plantId,
    refetchInterval: 60_000, // re-check every minute for countdown
  })

  // ── Tab 3: Ready for mix ──────────────────────────────────────────────────
  const { data: readyForMix = [], isLoading: loadingReady } = useQuery({
    queryKey: ['laydowns-ready', plantId],
    queryFn: () => laydownApi.byStatus('READY_FOR_MIX', plantId),
    enabled: !!plantId,
  })

  // ── Start conditioning ─────────────────────────────────────────────────────
  const [startTarget, setStartTarget] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [startForm] = Form.useForm()

  const startMutation = useMutation({
    mutationFn: (data: { id: string; values: any }) =>
      laydownApi.startConditioning(data.id, data.values),
    onSuccess: () => {
      message.success('Bales moved to Pre-Conditioning')
      qc.invalidateQueries({ queryKey: ['laydowns-approved'] })
      qc.invalidateQueries({ queryKey: ['conditioning-queue'] })
      setStartTarget(null)
      startForm.resetFields()
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const openStart = (laydown: any) => {
    setStartTarget(laydown)
    startForm.setFieldsValue({
      conditioning_days: 7,
      pre_godown_location: '',
    })
  }

  // ── Release conditioning ───────────────────────────────────────────────────
  const releaseMutation = useMutation({
    mutationFn: (data: { id: string; force_reason?: string }) =>
      laydownApi.releaseConditioning(data.id, { force_reason: data.force_reason }),
    onSuccess: () => {
      message.success('Bales released — Ready for Mix')
      qc.invalidateQueries({ queryKey: ['conditioning-queue'] })
      qc.invalidateQueries({ queryKey: ['laydowns-ready'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const handleRelease = (item: any) => {
    if (item.is_ready) {
      modal.confirm({
        title: 'Release for mixing?',
        content: `Conditioning complete (${item.conditioning_min_days} days). Mark as Ready for Mix?`,
        okText: 'Release',
        onOk: () => releaseMutation.mutate({ id: item.laydown_id }),
      })
    } else {
      // Force release with reason
      let reason = ''
      modal.confirm({
        title: 'Force Release Before Time',
        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        content: (
          <div>
            <p>This laydown still has <b>{item.days_left} day(s)</b> of conditioning left.</p>
            <p>Provide a reason to override:</p>
            <Input.TextArea
              rows={3}
              onChange={(e) => { reason = e.target.value }}
              placeholder="Why are you releasing early? (e.g. urgent production, weather conditioned faster)"
            />
          </div>
        ),
        okText: 'Force Release',
        okButtonProps: { danger: true },
        onOk: () => {
          if (reason.trim().length < 5) {
            message.error('Reason required (at least 5 chars)')
            throw new Error('reason required')
          }
          return releaseMutation.mutate({ id: item.laydown_id, force_reason: reason })
        },
      })
    }
  }

  // ── Tab 1 Columns: Approved ────────────────────────────────────────────────
  const approvedColumns = [
    { title: 'Laydown No', dataIndex: 'laydown_no', width: 140 },
    {
      title: 'Count', dataIndex: 'count_description', width: 120,
      render: (v?: string) => v || '—',
    },
    { title: 'Bales', dataIndex: 'actual_bales', width: 80 },
    {
      title: 'Weight (kg)', dataIndex: 'actual_weight_kg', width: 110,
      render: (v: any) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Approved', dataIndex: 'approved_at',
      render: (v?: string) => v ? dayjs(v).format('DD-MMM-YY HH:mm') : '—',
    },
    {
      title: '', key: 'actions', width: 220,
      render: (_: unknown, r: any) => (
        <Button type="primary" icon={<CloudUploadOutlined />}
          onClick={() => openStart(r)}>
          Move to Pre-Conditioning
        </Button>
      ),
    },
  ]

  // ── Tab 2: Conditioning Cards (visual countdown) ──────────────────────────
  const renderConditioningCard = (item: any) => {
    const totalMs = item.conditioning_release_due
      ? dayjs(item.conditioning_release_due).diff(dayjs(item.conditioning_started_at))
      : 0
    const elapsedMs = item.conditioning_started_at
      ? dayjs().diff(dayjs(item.conditioning_started_at))
      : 0
    const pct = totalMs > 0 ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100)) : 0

    const color = item.is_overdue ? '#ff4d4f' : item.is_ready ? '#52c41a' : '#faad14'

    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={item.laydown_id}>
        <Card
          size="small"
          style={{ borderLeft: `4px solid ${color}` }}
          actions={[
            <Button
              key="release"
              type={item.is_ready ? 'primary' : 'default'}
              icon={item.is_ready ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
              danger={!item.is_ready && !item.is_overdue}
              onClick={() => handleRelease(item)}
            >
              {item.is_ready ? 'Release for Mix' : 'Force Release'}
            </Button>,
          ]}
        >
          <Title level={5} style={{ margin: 0, fontFamily: 'monospace' }}>
            {item.laydown_no}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {item.count_description ?? '—'}
          </Text>
          {item.pre_godown_location && (
            <div style={{ marginTop: 4, fontSize: 11 }}>
              <Tag color="blue">{item.pre_godown_location}</Tag>
            </div>
          )}

          <Row gutter={8} style={{ marginTop: 12 }}>
            <Col span={12}>
              <Statistic title="Bales" value={item.actual_bales} valueStyle={{ fontSize: 18 }} />
            </Col>
            <Col span={12}>
              <Statistic
                title="Weight (kg)"
                value={Number(item.actual_weight_kg).toFixed(0)}
                valueStyle={{ fontSize: 18 }}
              />
            </Col>
          </Row>

          <div style={{ marginTop: 12 }}>
            <Progress
              percent={Math.round(pct)}
              status={item.is_overdue ? 'exception' : item.is_ready ? 'success' : 'active'}
              strokeColor={color}
              format={() =>
                item.is_overdue ? '⚠ Overdue' :
                item.is_ready ? '✓ Ready' :
                `${item.days_left}d left`
              }
            />
          </div>

          <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
            <div>Started: {dayjs(item.conditioning_started_at).format('DD-MMM HH:mm')}</div>
            <div>Due: {dayjs(item.conditioning_release_due).format('DD-MMM HH:mm')}</div>
            <div>Min Days: {item.conditioning_min_days}</div>
          </div>
        </Card>
      </Col>
    )
  }

  // ── Tab 3: Ready for Mix Columns ───────────────────────────────────────────
  const readyColumns = [
    { title: 'Laydown No', dataIndex: 'laydown_no', width: 140,
      render: (v: string) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text> },
    { title: 'Count', dataIndex: 'count_description' },
    { title: 'Bales', dataIndex: 'actual_bales', width: 80 },
    {
      title: 'Weight (kg)', dataIndex: 'actual_weight_kg', width: 110,
      render: (v: any) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Conditioning Done', dataIndex: 'conditioning_released_at',
      render: (v?: string) => v ? dayjs(v).format('DD-MMM-YY HH:mm') : '—',
    },
    {
      title: 'Pre-Godown', dataIndex: 'pre_godown_location',
      render: (v?: string) => v ? <Tag color="blue">{v}</Tag> : '—',
    },
    {
      title: 'Action', key: 'actions', width: 200,
      render: (_: unknown, r: any) => (
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={startExecutionMut.isPending}
          onClick={() => startExecutionMut.mutate(r.id)}
        >
          Start Execution
        </Button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Pre-Conditioning"
        subtitle="Move approved laydowns to pre-conditioning hall (7+ days), then release for mixing"
      />

      <Tabs
        items={[
          {
            key: 'approved',
            label: <span>
              Awaiting Conditioning
              {approved.length > 0 && <Badge count={approved.length} style={{ marginLeft: 8 }} />}
            </span>,
            children: (
              <Card>
                <Alert
                  message="These laydowns are approved by QC. Click 'Move to Pre-Conditioning' once bales are physically shifted to the pre-conditioning hall."
                  type="info" showIcon style={{ marginBottom: 12 }}
                />
                <Table
                  rowKey="id"
                  columns={approvedColumns}
                  dataSource={approved}
                  loading={loadingApproved}
                  size="middle"
                  locale={{ emptyText: <Empty description="No approved laydowns awaiting conditioning" /> }}
                />
              </Card>
            ),
          },
          {
            key: 'conditioning',
            label: <span>
              In Pre-Conditioning
              {conditioning.length > 0 && <Badge count={conditioning.length} style={{ marginLeft: 8 }} />}
            </span>,
            children: (
              <div>
                <Alert
                  message="Each card shows real-time conditioning progress. Click 'Release for Mix' once timer is complete (or override with reason if early)."
                  type="info" showIcon style={{ marginBottom: 12 }}
                />
                {loadingConditioning ? <div>Loading…</div> :
                  conditioning.length === 0
                    ? <Empty description="No laydowns in pre-conditioning" />
                    : <Row gutter={[16, 16]}>{conditioning.map(renderConditioningCard)}</Row>
                }
              </div>
            ),
          },
          {
            key: 'ready',
            label: <span>
              Ready for Mix
              {readyForMix.length > 0 && <Badge count={readyForMix.length} style={{ marginLeft: 8 }} />}
            </span>,
            children: (
              <Card>
                <Alert
                  message="These laydowns have completed conditioning. Open 'Laydown Execution' page to start mixing (QR scan + weight + moisture)."
                  type="success" showIcon style={{ marginBottom: 12 }}
                />
                <Table
                  rowKey="id"
                  columns={readyColumns}
                  dataSource={readyForMix}
                  loading={loadingReady}
                  size="middle"
                  locale={{ emptyText: <Empty description="No laydowns ready for mix" /> }}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* Start Conditioning Modal */}
      <Modal
        title={`Move to Pre-Conditioning: ${startTarget?.laydown_no ?? ''}`}
        open={!!startTarget}
        onCancel={() => setStartTarget(null)}
        onOk={() => startForm.validateFields().then(v =>
          startMutation.mutate({ id: startTarget.id, values: v })
        )}
        confirmLoading={startMutation.isPending}
        okText="Move to Pre-Conditioning"
      >
        <Alert
          message="QC: Confirm physical shifting of all bales in this laydown to the pre-conditioning hall."
          type="warning" showIcon style={{ marginBottom: 12 }}
        />
        <Form form={startForm} layout="vertical">
          <Form.Item
            name="conditioning_days"
            label="Conditioning Days (minimum 7 recommended)"
            rules={[{ required: true }]}
            help="QC may extend based on weather/humidity conditions"
          >
            <InputNumber min={1} max={30} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="pre_godown_location"
            label="Pre-Godown Hall / Location"
            rules={[{ required: true }]}
            help="Where the bales are physically placed (e.g. 'Hall-A', 'Pre-Cond-1')"
          >
            <Input placeholder="Hall-A" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
